import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import AuthLayout, { formatApiError } from "@/components/AuthLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Users land here from the "Confirm my email" button in the verification
 * email. The `:token` param is opaque; we POST it to /auth/verify-email and
 * show the result. On success, refresh the AuthContext so the "confirm your
 * email" banner disappears everywhere.
 */
export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "working", message: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState({ status: "ok", message: "Your email is confirmed. You can plant seeds now." });
        await refresh();
      } catch (err) {
        setState({ status: "error", message: formatApiError(err) });
      }
    })();
  }, [token, refresh]);

  return (
    <AuthLayout
      title={
        state.status === "ok"
          ? "You're all set."
          : state.status === "error"
            ? "This link didn't work."
            : "Confirming your email…"
      }
    >
      <div className="flex flex-col items-center text-center py-4" data-testid="verify-email-result">
        {state.status === "working" && <Loader2 className="w-8 h-8 text-[#4E5A3D] animate-spin" />}
        {state.status === "ok" && <CheckCircle2 className="w-10 h-10 text-[#4E5A3D]" strokeWidth={1.5} />}
        {state.status === "error" && <XCircle className="w-10 h-10 text-[#8a3a2a]" strokeWidth={1.5} />}
        {state.message && (
          <p className="mt-4 text-sm text-[#2A2823]/75 leading-relaxed">{state.message}</p>
        )}
        {state.status === "ok" && (
          <Button
            data-testid="verify-email-continue"
            onClick={() => navigate("/grove")}
            className="mt-6 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-7 h-11"
          >
            Enter The Grove
          </Button>
        )}
        {state.status === "error" && (
          <div className="mt-6 text-sm text-[#2A2823]/70">
            <Link to="/login" className="text-[#4E5A3D] underline underline-offset-2">Sign in</Link>{" "}
            and we'll send you a fresh link.
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
